import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { ArrowLeft, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getPromoCode, updatePromoCode, listRestaurants, listGroceries, getResortLocations } from '../services/resortAdminApi';
import { MultiSelect } from '../components/ui/MultiSelect';
import { validatePromoCodeForm, getValidationErrorMessage } from '../utils/promoCodeValidation';

interface MerchantInfo {
  type: string;
  name: string;
  display_name?: string;
}

interface PromoCodeData {
  name: string;
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  min_order_amount: number;
  max_order_amount: number;
  restaurant?: string;
  restaurant_name?: string;
  merchants?: MerchantInfo[];
  resorts?: { name: string; display_name: string }[];
  merchants_json?: string;
  resorts_json?: string;
  max_uses_per_user: number;
  total_usage_count: number;
  start_date: string;
  end_date: string;
  status: 'Active' | 'Inactive';
  is_redeemed: number;
  redeemed_by_order?: string;
  redeemed_at?: string;
  created_by_admin?: string;
  batch_id?: string;
  creation?: string;
}

interface MerchantOption {
  value: string;
  label: string;
  group: string;
  type: string;
}

interface ResortOption {
  value: string;
  label: string;
}

export const PromoCodeDetails: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();
  const [promoCode, setPromoCode] = useState<PromoCodeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [merchantOptions, setMerchantOptions] = useState<MerchantOption[]>([]);
  const [resortOptions, setResortOptions] = useState<ResortOption[]>([]);

  const [formData, setFormData] = useState({
    discount_type: 'fixed' as 'fixed' | 'percentage',
    discount_value: '' as number | '',
    min_order_amount: 0,
    max_order_amount: 0,
    selectedMerchants: [] as string[],
    selectedResorts: [] as string[],
    max_uses_per_user: 0 as number | '',
    start_date: '',
    end_date: '',
    status: 'Active' as 'Active' | 'Inactive'
  });

  useEffect(() => {
    loadPromoCode();
  }, [name]);

  // Load merchant and resort options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [restaurantsResult, groceriesResult, resortsResult] = await Promise.all([
          listRestaurants(undefined, 1, 200).catch(() => null),
          listGroceries(undefined, 1, 200).catch(() => null),
          getResortLocations().catch(() => null),
        ]);

        const merchants: MerchantOption[] = [];

        const restaurantsData = restaurantsResult?.data || restaurantsResult || [];
        restaurantsData.forEach((r: any) => {
          merchants.push({
            value: `Restaurant:${r.name}`,
            label: r.restaurant_name || r.name,
            group: 'Restaurants',
            type: 'Restaurant',
          });
        });

        const groceriesData = groceriesResult?.data || groceriesResult || [];
        groceriesData.forEach((g: any) => {
          merchants.push({
            value: `Grocery:${g.name}`,
            label: g.grocery_name || g.name,
            group: 'Groceries',
            type: 'Grocery',
          });
        });

        setMerchantOptions(merchants);

        const resortLocations = resortsResult?.resort_locations || [];
        setResortOptions(resortLocations.map((r: any) => ({
          value: r.value,
          label: r.label || r.value,
        })));
      } catch (err) {
        console.error('Failed to load merchant/resort options:', err);
      }
    };
    loadOptions();
  }, []);

  const loadPromoCode = async () => {
    if (!name) return;

    setLoading(true);
    try {
      const data = await getPromoCode(name);
      setPromoCode(data);

      // Convert merchants data to selected values
      const selectedMerchants: string[] = [];
      if (data.merchants && data.merchants.length > 0) {
        data.merchants.forEach((m: MerchantInfo) => {
          selectedMerchants.push(`${m.type}:${m.name}`);
        });
      }

      // Convert resorts data to selected values
      const selectedResorts: string[] = [];
      if (data.resorts && data.resorts.length > 0) {
        data.resorts.forEach((r: { name: string }) => {
          selectedResorts.push(r.name);
        });
      }

      setFormData({
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        min_order_amount: data.min_order_amount || 0,
        max_order_amount: data.max_order_amount || 0,
        selectedMerchants,
        selectedResorts,
        max_uses_per_user: data.max_uses_per_user || 0,
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        status: data.status
      });
    } catch (error: any) {
      setError(error.message || 'Failed to load promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    setSaving(true);
    setError('');
    setSuccess('');

    // Redeemed single-use codes are locked for editing, but their status can still be
    // changed (e.g. deactivated). Send a status-only update and skip the full-form validation.
    if (!isEditable) {
      try {
        await updatePromoCode({ name, status: formData.status });
        setSuccess('Promo code status updated successfully');
        loadPromoCode();
      } catch (error: any) {
        setError(error.message || 'Failed to update status');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Frontend validation
    const validationErrors = validatePromoCodeForm(formData, true);
    if (validationErrors.length > 0) {
      setError(getValidationErrorMessage(validationErrors));
      setSaving(false);
      return;
    }

    // At this point, validation passed, so discount_value must be a valid number
    if (formData.discount_value === '' || formData.discount_value === undefined) {
      setError('The discount value is required and cannot be 0');
      setSaving(false);
      return;
    }

    try {
      // Convert selected merchant values to API format
      const merchants = formData.selectedMerchants.length > 0
        ? formData.selectedMerchants.map(v => {
            const [type, ...nameParts] = v.split(':');
            return { type, name: nameParts.join(':') };
          })
        : [];
      const resorts = formData.selectedResorts.length > 0 ? formData.selectedResorts : [];

      await updatePromoCode({
        name,
        discount_type: formData.discount_type,
        discount_value: formData.discount_value as number,
        min_order_amount: formData.min_order_amount,
        max_order_amount: formData.max_order_amount,
        restaurant: null,
        merchants,
        resorts,
        max_uses_per_user: formData.max_uses_per_user === '' ? 0 : formData.max_uses_per_user,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        status: formData.status,
      });
      setSuccess('Promo code updated successfully');
      loadPromoCode();
    } catch (error: any) {
      // Extract user-friendly error message
      let errorMessage = 'Failed to update promo code';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?._error_message) {
        errorMessage = error.response.data._error_message;
      } else if (error.response?.data?.exception) {
        errorMessage = error.response.data.exception;
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!promoCode) {
    return <div className="text-center py-8 text-red-600">Promo code not found</div>;
  }

  const isEditable = !promoCode.is_redeemed || (promoCode.max_uses_per_user > 0);

  // Build merchant/resort display for info panel
  const merchantsDisplay = promoCode.merchants && promoCode.merchants.length > 0
    ? promoCode.merchants.map(m => m.display_name || m.name).join(', ')
    : null;
  const resortsDisplay = promoCode.resorts && promoCode.resorts.length > 0
    ? promoCode.resorts.map(r => r.display_name || r.name).join(', ')
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/promo-codes')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Promo Code: <span className="font-mono text-indigo-600">{promoCode.code}</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditable ? 'Edit promo code settings' : 'This code has been redeemed — only its status can be changed'}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Edit Form */}
        <div className="lg:col-span-2">
          <Card>
            <form onSubmit={handleSave} className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'fixed' | 'percentage' })}
                    disabled={!isEditable || isReadOnly}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="fixed">Fixed Amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value === '' ? '' : formData.discount_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        discount_value: value === '' ? '' : parseFloat(value) || ''
                      });
                    }}
                    disabled={!isEditable || isReadOnly}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum Subtotal Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) || 0 })}
                  disabled={!isEditable || isReadOnly}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Maximum Subtotal Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.max_order_amount}
                  onChange={(e) => setFormData({ ...formData, max_order_amount: parseFloat(e.target.value) || 0 })}
                  disabled={!isEditable || isReadOnly}
                  className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed ${formData.max_order_amount > 0 && formData.min_order_amount > 0 && formData.max_order_amount < formData.min_order_amount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formData.max_order_amount > 0 && formData.min_order_amount > 0 && formData.max_order_amount < formData.min_order_amount ? (
                  <p className="mt-1 text-xs text-red-500">Max subtotal amount cannot be less than min subtotal amount</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">0 = no limit</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Merchant</label>
                <MultiSelect
                  options={merchantOptions}
                  selected={formData.selectedMerchants}
                  onChange={(selected) => setFormData({ ...formData, selectedMerchants: selected })}
                  placeholder="All merchants"
                  disabled={!isEditable || isReadOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Resorts</label>
                <MultiSelect
                  options={resortOptions}
                  selected={formData.selectedResorts}
                  onChange={(selected) => setFormData({ ...formData, selectedResorts: selected })}
                  placeholder="All resorts"
                  disabled={!isEditable || isReadOnly}
                />
                <p className="mt-1 text-xs text-gray-500">Valid for all merchants in selected resorts</p>
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.max_uses_per_user !== 0}
                    onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.checked ? '' : 0 })}
                    disabled={!isEditable || isReadOnly}
                    className="rounded border-gray-300 text-alps-primary focus:ring-alps-primary disabled:cursor-not-allowed"
                  />
                  <span className="text-sm font-medium text-gray-700">Multi-use code</span>
                </label>
                {formData.max_uses_per_user !== 0 ? (
                  <div className="mt-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.max_uses_per_user === -1}
                        onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.checked ? -1 : '' })}
                        disabled={!isEditable || isReadOnly}
                        className="rounded border-gray-300 text-alps-primary focus:ring-alps-primary disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-gray-700">Infinite use</span>
                    </label>
                    {formData.max_uses_per_user !== -1 && (
                      <div className="mt-2">
                        <label className="block text-xs text-gray-500 mb-1">Uses per user</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={formData.max_uses_per_user === '' ? '' : formData.max_uses_per_user}
                          placeholder="Set how many times each user can use this code"
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData({ ...formData, max_uses_per_user: val === '' ? '' : Math.max(1, parseInt(val) || 1) });
                          }}
                          disabled={!isEditable || isReadOnly}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">The code can be used only once</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    disabled={!isEditable || isReadOnly}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    disabled={!isEditable || isReadOnly}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  disabled={isReadOnly}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {!isReadOnly && (
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : isEditable ? 'Save Changes' : 'Save Status'}
                  </button>
                </div>
              )}
            </form>
          </Card>
        </div>

        {/* Info Panel */}
        <div>
          <Card>
            <div className="p-4 space-y-4">
              <h3 className="font-medium text-gray-900">Code Information</h3>

              <div>
                <span className="text-sm text-gray-500">Code</span>
                <p className="font-mono font-medium text-indigo-600">{promoCode.code}</p>
              </div>

              <div>
                <span className="text-sm text-gray-500">Status</span>
                <p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    promoCode.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {promoCode.status}
                  </span>
                </p>
              </div>

              <div>
                <span className="text-sm text-gray-500">Merchant(s)</span>
                <p className="text-sm font-medium">{merchantsDisplay || 'All merchants'}</p>
              </div>

              {resortsDisplay && (
                <div>
                  <span className="text-sm text-gray-500">Resorts</span>
                  <p className="text-sm font-medium">{resortsDisplay}</p>
                </div>
              )}

              {promoCode.max_order_amount > 0 && (
                <div>
                  <span className="text-sm text-gray-500">Max Subtotal Amount</span>
                  <p className="text-sm font-medium">&euro;{promoCode.max_order_amount}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">Usage Mode</span>
                <p className="text-sm font-medium">
                  {promoCode.max_uses_per_user === 0
                    ? 'Single-use'
                    : promoCode.max_uses_per_user === -1
                    ? 'Multi-use, unlimited'
                    : `Multi-use, ${promoCode.max_uses_per_user}x per user`}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {promoCode.max_uses_per_user === 0
                    ? 'One use by one person, then the code is burned'
                    : promoCode.max_uses_per_user === -1
                    ? 'Any user can use this code unlimited times'
                    : `Any user can use this code up to ${promoCode.max_uses_per_user} time${promoCode.max_uses_per_user === 1 ? '' : 's'}`}
                </p>
              </div>

              {promoCode.max_uses_per_user !== 0 && (
                <div>
                  <span className="text-sm text-gray-500">Total Uses</span>
                  <p className="text-sm font-medium">{promoCode.total_usage_count}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">Redeemed</span>
                <p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    promoCode.is_redeemed ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {promoCode.is_redeemed ? 'Yes' : 'No'}
                  </span>
                </p>
              </div>

              {promoCode.redeemed_by_order && (
                <div>
                  <span className="text-sm text-gray-500">Used by Order</span>
                  <p className="text-sm font-medium">{promoCode.redeemed_by_order}</p>
                </div>
              )}

              {promoCode.redeemed_at && (
                <div>
                  <span className="text-sm text-gray-500">Redeemed At</span>
                  <p className="text-sm">{new Date(promoCode.redeemed_at).toLocaleString()}</p>
                </div>
              )}

              {promoCode.batch_id && (
                <div>
                  <span className="text-sm text-gray-500">Batch ID</span>
                  <p className="text-sm font-mono">{promoCode.batch_id}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">Created</span>
                <p className="text-sm">{promoCode.creation ? new Date(promoCode.creation).toLocaleString() : '-'}</p>
              </div>

              {promoCode.created_by_admin && (
                <div>
                  <span className="text-sm text-gray-500">Created By</span>
                  <p className="text-sm">{promoCode.created_by_admin}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
